import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PROJECT_TYPES, PROJECT_FORMATS, PROJECT_STATUSES } from "@/lib/constants";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { Plus, X, Loader2, ChevronRight, Pencil } from "lucide-react";

const HEALTH_DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
const HEALTH_LABEL = { green: "On track", amber: "Behind 1–2", red: "Behind 3+" };

export default function ProjectDashboard() {
  const { isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerId, setDrawerId] = useState(null);

  const load = async () => {
    setLoading(true);
    const [pRes, rRes] = await Promise.all([
      api.get("/projects/list"), api.get("/reference"),
    ]);
    setProjects(pRes.data);
    setClients(rRes.data.clients);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openProject = async (p) => {
    const r = await api.get(`/projects/${p.id}`);
    setSelected(r.data);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Phase 2</div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">Project Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1.5">Track scope and health across every client project.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setCreating(true)} data-testid="create-project-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New project
          </button>
        )}
      </header>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map(p => (
            <button
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => openProject(p)}
              className="text-left bg-white border border-gray-200 rounded-lg p-5 hover:border-navy/40 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">{p.client_name}</div>
                  <div className="text-base font-semibold text-gray-900 mt-1 tracking-tight">{p.name}</div>
                </div>
                <span className={`w-2 h-2 rounded-full mt-2 ${HEALTH_DOT[p.health]}`} title={HEALTH_LABEL[p.health]} />
              </div>
              <div className="flex items-center gap-3 mt-4">
                <div className="text-2xl font-mono font-semibold text-gray-900">{p.delivered}<span className="text-gray-400">/{p.total}</span></div>
                <div className="text-xs text-gray-500 leading-tight">
                  delivered<br/>
                  <span className="text-gray-400">{HEALTH_LABEL[p.health]}</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-2">
                  {p.project_status && <StatusBadgeSimple text={p.project_status} />}
                  {p.project_type && <span className="text-gray-500">{p.project_type}</span>}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-navy" />
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <ProjectDetail
        project={selected}
        onClose={() => setSelected(null)}
        onEdit={() => { setEditing(selected); setSelected(null); }}
        onTaskOpen={(id) => setDrawerId(id)}
        isAdmin={isAdmin}
      />}
      {creating && <ProjectForm clients={clients} onClose={() => setCreating(false)} onSaved={load} />}
      {editing && <ProjectForm project={editing} clients={clients} onClose={() => setEditing(null)} onSaved={load} />}
      <TaskDetailDrawer
        taskId={drawerId}
        onClose={() => setDrawerId(null)}
        onChanged={load}
        onEdit={() => {}}
      />
    </div>
  );
}

function StatusBadgeSimple({ text }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border border-gray-200 bg-gray-50 text-gray-700 uppercase tracking-wider">{text}</span>;
}

function ProjectDetail({ project, onClose, onEdit, onTaskOpen, isAdmin }) {
  return (
    <div className="fixed inset-0 z-40 bg-gray-900/30 backdrop-blur-sm" onClick={onClose}>
      <aside data-testid="project-detail-drawer" className="fixed inset-y-0 right-0 w-full max-w-[700px] bg-white shadow-2xl border-l border-gray-200 flex flex-col animate-slide-in-right" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Project</div>
          <div className="flex items-center gap-2">
            {isAdmin && <button onClick={onEdit} data-testid="project-edit-btn" className="text-sm text-navy hover:underline inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">{project.client_name}</div>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1">{project.name}</h2>
            <div className="flex items-center gap-2 mt-3 flex-wrap text-xs">
              {project.project_type && <span className="px-2 py-0.5 rounded border border-gray-200 text-gray-700">{project.project_type}</span>}
              {project.project_format && <span className="px-2 py-0.5 rounded border border-gray-200 text-gray-700">{project.project_format}</span>}
              {project.project_status && <span className="px-2 py-0.5 rounded border border-navy/30 bg-navy/5 text-navy font-medium">{project.project_status}</span>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Stat label="Delivered" value={project.delivered} />
            <Stat label="Total scope" value={project.total} />
            <Stat label="Health" value={<span className={`inline-flex items-center gap-1.5`}><span className={`w-2 h-2 rounded-full ${HEALTH_DOT[project.health]}`} />{HEALTH_LABEL[project.health]}</span>} />
          </div>

          {project.notes && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Notes</div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3">{project.notes}</p>
            </div>
          )}

          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">Tasks ({project.tasks.length})</div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {project.tasks.length === 0 ? (
                <div className="p-6 text-sm text-gray-400 italic">No tasks linked.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {project.tasks.map(t => (
                      <tr key={t.id} onClick={() => onTaskOpen(t.id)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{t.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{t.assignee_name} · Due {t.due_date}</div>
                        </td>
                        <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-1 font-mono">{value}</div>
    </div>
  );
}

function ProjectForm({ project, clients, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project?.name || "",
    client_name: project?.client_name || "",
    project_type: project?.project_type || "Content IP",
    project_format: project?.project_format || "Not Applicable",
    project_status: project?.project_status || "Active",
    notes: project?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (project) await api.patch(`/projects/${project.id}`, form);
      else {
        const r = await api.post("/projects", { name: form.name, client_name: form.client_name });
        await api.patch(`/projects/${r.data.id}`, form);
      }
      onSaved && onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="project-form-modal" className="bg-white max-w-lg w-full rounded-xl shadow-xl border border-gray-200 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold tracking-tight">{project ? "Edit project" : "New project"}</h2>
        <Field label="Name"><input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="project-name-input" className="input" /></Field>
        <Field label="Client">
          <select required value={form.client_name} onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))} data-testid="project-client-select" className="input" disabled={!!project}>
            <option value="">Select…</option>
            {clients.map(c => <option key={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Type"><select value={form.project_type} onChange={(e) => setForm(f => ({ ...f, project_type: e.target.value }))} className="input">{PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Format"><select value={form.project_format} onChange={(e) => setForm(f => ({ ...f, project_format: e.target.value }))} className="input">{PROJECT_FORMATS.map(t => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Status"><select value={form.project_status} onChange={(e) => setForm(f => ({ ...f, project_status: e.target.value }))} data-testid="project-status-select" className="input">{PROJECT_STATUSES.map(t => <option key={t}>{t}</option>)}</select></Field>
        </div>
        <Field label="Notes"><textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input" /></Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="project-save-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
        </div>
        <style>{`.input{width:100%;background:white;border:1px solid #d1d5db;border-radius:6px;padding:8px 10px;font-size:14px;color:#111827;outline:none}.input:focus{border-color:#2B4462;box-shadow:0 0 0 3px rgba(43,68,98,.12)}.input:disabled{background:#f9fafb;color:#6b7280}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
